import React from 'react';
import { ShoppingCart, Minus, Plus, Trash2, ArrowLeft, FileText, Edit3 } from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { formatAmount } from '../../utils/format';
import { calcTotals, itbisLabel } from '../../utils/tax';
const CustomerCartView = ({ cart, tenant, onUpdateQuantity, onRemoveItem, onUpdateNotes, onCheckout, onBack }) => {
  const { subtotal, tax, total } = calcTotals(cart.reduce((sum, item) => sum + (item.price * item.quantity), 0), tenant);
  const [editingNotes, setEditingNotes] = React.useState(null);
  const [notesText, setNotesText] = React.useState('');

  const startEditing = (item) => {
    setEditingNotes(`${item.id}-${item.variant_id || 'no-variant'}`);
    setNotesText(item.notes || '');
  };

  const saveNotes = (item) => {
    onUpdateNotes(item.id, notesText, item.variant_id);
    setEditingNotes(null);
  };

  return (
    <div className="pb-4 min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white sticky top-0 z-10 border-b border-gray-100">
        <div className="flex items-center gap-3 px-4 h-14">
          <button onClick={onBack} className="p-2 -ml-2 hover:bg-gray-100 rounded-full active:scale-90 transition-transform">
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <h1 className="text-lg font-bold text-gray-800">Mi Carrito</h1>
          <span className="text-sm text-gray-500 ml-auto">{cart.length} items</span>
        </div>
      </div>

      {cart.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShoppingCart className="w-10 h-10 text-orange-400" />
          </div>
          <p className="text-gray-600 font-medium">Tu carrito está vacío</p>
          <p className="text-sm text-gray-400 mt-1">Agrega productos del menú</p>
          <Button onClick={onBack} className="mt-6 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl">
            Ver Menú
          </Button>
        </div>
      ) : (
        <>
          {/* Cart Items */}
          <div className="px-4 py-4 space-y-3">
            {cart.map((item) => {
              const itemKey = `${item.id}-${item.variant_id || 'no-variant'}`;
              const isEditing = editingNotes === itemKey;

              return (
                <div key={itemKey} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-50">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-800 text-sm truncate">{item.name}</h4>
                      {item.variant_name && (
                        <p className="text-xs text-orange-500 font-medium">{item.variant_name}</p>
                      )}
                      <p className="text-sm font-bold text-orange-600 mt-0.5">
                        ${formatAmount(item.price * item.quantity)}
                      </p>
                    </div>

                    {/* Quantity Controls */}
                    <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
                      <button
                        onClick={() => onUpdateQuantity(item.id, -1, item.variant_id)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white active:scale-90 transition-all"
                      >
                        <Minus className="w-4 h-4 text-gray-600" />
                      </button>
                      <span className="w-8 text-center font-bold text-gray-800 text-sm">{item.quantity}</span>
                      <button
                        onClick={() => onUpdateQuantity(item.id, 1, item.variant_id)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white active:scale-90 transition-all"
                      >
                        <Plus className="w-4 h-4 text-gray-600" />
                      </button>
                    </div>

                    <button
                      onClick={() => onRemoveItem(item.id, item.variant_id)}
                      className="p-2 hover:bg-red-50 rounded-full active:scale-90 transition-all"
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                  </div>

                  {/* Notes */}
                  {isEditing ? (
                    <div className="mt-3 space-y-2">
                      <Label className="text-xs text-gray-500 flex items-center gap-1">
                        <FileText className="w-3 h-3" /> Nota:
                      </Label>
                      <Input
                        value={notesText}
                        onChange={(e) => setNotesText(e.target.value)}
                        placeholder="Ej: Sin cebolla, extra picante..."
                        className="text-sm rounded-xl bg-gray-50 border-0"
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingNotes(null)}
                          className="flex-1 h-8 text-xs rounded-lg"
                        >
                          Cancelar
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => saveNotes(item)}
                          className="flex-1 h-8 text-xs bg-green-500 hover:bg-green-600 rounded-lg"
                        >
                          Guardar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => startEditing(item)}
                      className="mt-2 flex items-center gap-1 text-xs text-blue-500 hover:text-blue-600 active:scale-95 transition-transform"
                    >
                      <Edit3 className="w-3 h-3" />
                      {item.notes ? `Nota: ${item.notes}` : 'Agregar nota'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Summary & Checkout */}
          <div className="fixed bottom-16 left-0 right-0 bg-white border-t border-gray-100 px-4 py-3 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
            <div className="max-w-lg mx-auto">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-500">Subtotal</span>
                <span className="font-medium text-gray-800">${formatAmount(subtotal)}</span>
              </div>
              {tax > 0 && (
                <div className="flex justify-between text-sm mb-3">
                  <span className="text-gray-500">{itbisLabel()}</span>
                  <span className="font-medium text-gray-800">${formatAmount(tax)}</span>
                </div>
              )}
              <div className="flex items-center justify-between mb-3">
                <span className="text-lg font-bold text-gray-800">Total</span>
                <span className="text-xl font-bold text-orange-600">${formatAmount(total)}</span>
              </div>
              <Button
                onClick={onCheckout}
                className="w-full h-12 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold rounded-xl shadow-lg active:scale-[0.98] transition-transform"
              >
                Proceder al Pago
              </Button>
            </div>
          </div>
          <div className="h-32" /> {/* Spacer for fixed bottom bar */}
        </>
      )}
    </div>
  );
};

export default CustomerCartView;
